// Design System Transition Tokens
// Consistent animation and transition system

// Duration scale
export const durations = {
  instant: '0ms',
  fast: '150ms',
  normal: '200ms',
  slow: '300ms',
  slower: '500ms',
  slowest: '700ms',
};

// Easing functions
export const easings = {
  // Standard easing curves
  linear: 'linear',
  ease: 'ease',
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  
  // Custom cubic-bezier easing functions
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  
  // Material Design easing
  materialStandard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  materialDecelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  materialAccelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
  materialSharp: 'cubic-bezier(0.4, 0.0, 0.6, 1)',
};

// Transition properties
export const properties = {
  all: 'all',
  transform: 'transform',
  opacity: 'opacity',
  background: 'background-color',
  border: 'border-color',
  color: 'color',
  boxShadow: 'box-shadow',
  width: 'width',
  height: 'height',
  margin: 'margin',
  padding: 'padding',
  fontSize: 'font-size',
  letterSpacing: 'letter-spacing',
};

// Semantic transition tokens
export const transitions = {
  // Component transitions
  component: {
    button: {
      property: [properties.background, properties.border, properties.boxShadow, properties.transform],
      duration: durations.fast,
      easing: easings.smooth,
    },
    buttonHover: {
      property: [properties.transform],
      duration: durations.fast,
      easing: easings.bounce,
    },
    input: {
      property: [properties.border, properties.boxShadow],
      duration: durations.normal,
      easing: easings.smooth,
    },
    card: {
      property: [properties.boxShadow, properties.transform],
      duration: durations.normal,
      easing: easings.smooth,
    },
    cardHover: {
      property: [properties.transform, properties.boxShadow],
      duration: durations.fast,
      easing: easings.smooth,
    },
    modal: {
      property: [properties.opacity, properties.transform],
      duration: durations.slow,
      easing: easings.emphasized,
    },
    tooltip: {
      property: [properties.opacity, properties.transform],
      duration: durations.fast,
      easing: easings.smooth,
    },
    dropdown: {
      property: [properties.opacity, properties.transform],
      duration: durations.normal,
      easing: easings.emphasized,
    },
  },
  
  // Layout transitions
  layout: {
    sidebar: {
      property: [properties.transform],
      duration: durations.slow,
      easing: easings.emphasized,
    },
    page: {
      property: [properties.opacity],
      duration: durations.normal,
      easing: easings.smooth,
    },
    navigation: {
      property: [properties.background, properties.color],
      duration: durations.fast,
      easing: easings.smooth,
    },
  },
  
  // Interaction transitions
  interaction: {
    hover: {
      property: [properties.all],
      duration: durations.fast,
      easing: easings.smooth,
    },
    focus: {
      property: [properties.boxShadow, properties.border],
      duration: durations.fast,
      easing: easings.smooth,
    },
    active: {
      property: [properties.transform],
      duration: durations.instant,
      easing: easings.sharp,
    },
    disabled: {
      property: [properties.opacity],
      duration: durations.normal,
      easing: easings.smooth,
    },
  },
  
  // State transitions
  state: {
    fadeIn: {
      property: [properties.opacity],
      duration: durations.normal,
      easing: easings.smooth,
    },
    fadeOut: {
      property: [properties.opacity],
      duration: durations.fast,
      easing: easings.smooth,
    },
    slideIn: {
      property: [properties.transform, properties.opacity],
      duration: durations.slow,
      easing: easings.emphasized,
    },
    slideOut: {
      property: [properties.transform, properties.opacity],
      duration: durations.normal,
      easing: easings.sharp,
    },
    scaleIn: {
      property: [properties.transform, properties.opacity],
      duration: durations.normal,
      easing: easings.bounce,
    },
    scaleOut: {
      property: [properties.transform, properties.opacity],
      duration: durations.fast,
      easing: easings.sharp,
    },
  },
};

// Transition utilities
export const transitionUtils = {
  // Create transition string
  create: (property, duration = 'normal', easing = 'smooth') => {
    const prop = Array.isArray(property) ? property.join(', ') : property;
    const dur = durations[duration] || duration;
    const ease = easings[easing] || easing;
    return `${prop} ${dur} ${ease}`;
  },
  
  // Get component transition
  getComponentTransition: (component, state = 'default') => {
    const key = state === 'default' ? component : `${component}${state.charAt(0).toUpperCase()}${state.slice(1)}`;
    const config = transitions.component[key] || transitions.component[component];
    
    if (!config) return transitions.component.button;
    
    return transitionUtils.create(config.property, config.duration, config.easing);
  },
  
  // Get layout transition
  getLayoutTransition: (layout) => {
    const config = transitions.layout[layout];
    if (!config) return transitions.layout.page;
    
    return transitionUtils.create(config.property, config.duration, config.easing);
  },
  
  // Get interaction transition
  getInteractionTransition: (interaction) => {
    const config = transitions.interaction[interaction];
    if (!config) return transitions.interaction.hover;
    
    return transitionUtils.create(config.property, config.duration, config.easing);
  },
  
  // Get state transition
  getStateTransition: (state) => {
    const config = transitions.state[state];
    if (!config) return transitions.state.fadeIn;
    
    return transitionUtils.create(config.property, config.duration, config.easing);
  },
  
  // Create multiple transitions
  createMultiple: (configs) => {
    return configs.map(config => {
      if (typeof config === 'string') return config;
      return transitionUtils.create(config.property, config.duration, config.easing);
    }).join(', ');
  },
};

// Common transition combinations
export const transitionCombinations = {
  // Button transitions
  button: {
    default: transitionUtils.getComponentTransition('button'),
    hover: transitionUtils.getComponentTransition('button', 'hover'),
    focus: transitionUtils.getInteractionTransition('focus'),
    active: transitionUtils.getInteractionTransition('active'),
  },
  
  // Input transitions
  input: {
    default: transitionUtils.getComponentTransition('input'),
    focus: transitionUtils.getInteractionTransition('focus'),
    hover: transitionUtils.getInteractionTransition('hover'),
  },
  
  // Card transitions
  card: {
    default: transitionUtils.getComponentTransition('card'),
    hover: transitionUtils.getComponentTransition('card', 'hover'),
  },
  
  // Modal transitions
  modal: {
    enter: transitionUtils.getStateTransition('slideIn'),
    exit: transitionUtils.getStateTransition('slideOut'),
  },
  
  // General purpose
  general: {
    smooth: transitionUtils.create('all', 'normal', 'smooth'),
    fast: transitionUtils.create('all', 'fast', 'smooth'),
    slow: transitionUtils.create('all', 'slow', 'smooth'),
  },
};

// Animation keyframes (for CSS-in-JS usage)
export const keyframes = {
  // Fade animations
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeOut: {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
  
  // Slide animations
  slideInUp: {
    from: { transform: 'translateY(20px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
  },
  slideInDown: {
    from: { transform: 'translateY(-20px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
  },
  slideInLeft: {
    from: { transform: 'translateX(-20px)', opacity: 0 },
    to: { transform: 'translateX(0)', opacity: 1 },
  },
  slideInRight: {
    from: { transform: 'translateX(20px)', opacity: 0 },
    to: { transform: 'translateX(0)', opacity: 1 },
  },
  
  // Scale animations
  scaleIn: {
    from: { transform: 'scale(0.9)', opacity: 0 },
    to: { transform: 'scale(1)', opacity: 1 },
  },
  scaleOut: {
    from: { transform: 'scale(1)', opacity: 1 },
    to: { transform: 'scale(0.9)', opacity: 0 },
  },
  
  // Bounce animation
  bounce: {
    '0%, 20%, 53%, 80%, 100%': { transform: 'translate3d(0,0,0)' },
    '40%, 43%': { transform: 'translate3d(0, -30px, 0)' },
    '70%': { transform: 'translate3d(0, -15px, 0)' },
    '90%': { transform: 'translate3d(0, -4px, 0)' },
  },
  
  // Pulse animation
  pulse: {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' },
    '100%': { transform: 'scale(1)' },
  },
  
  // Shake animation
  shake: {
    '10%, 90%': { transform: 'translate3d(-1px, 0, 0)' },
    '20%, 80%': { transform: 'translate3d(2px, 0, 0)' },
    '30%, 50%, 70%': { transform: 'translate3d(-4px, 0, 0)' },
    '40%, 60%': { transform: 'translate3d(4px, 0, 0)' },
  },
};

export default {
  durations,
  easings,
  properties,
  semantic: transitions,
  utils: transitionUtils,
  combinations: transitionCombinations,
  keyframes,
};